"use client";

/* eslint-disable @next/next/no-img-element */
import { useEffect, useRef, useState, useCallback } from "react";
import { motion, useScroll, useSpring, useTransform, AnimatePresence, PanInfo } from "motion/react";
import { useViewportHeight } from "@/app/hooks/useViewportHeight";
import { springConfig } from "@/app/hooks/useSpringConfig";
import cardsData from "./cards.json";

const SWIPE_CONFIDENCE_THRESHOLD = 10000;
const MOBILE_BREAKPOINT = 1024;
const SCROLL_OFFSETS_MOBILE = [0, 0.25, 0.65, 1];
const SCROLL_OFFSETS_DESKTOP = [0, 0.18, 0.58, 1];

const Arbeiten = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [[page, direction], setPage] = useState([0, 0]);
    const [isMobile, setIsMobile] = useState(false);
    const cardsPerSlide = isMobile ? 1 : 3;
    const totalSlides = Math.ceil(cardsData.length / cardsPerSlide);

    const sectionRef = useRef<HTMLDivElement | null>(null);
    const viewportHeight = useViewportHeight();

    useEffect(() => {
        const updateMobile = () => {
            setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
        };

        updateMobile();
        window.addEventListener("resize", updateMobile);
        return () => window.removeEventListener("resize", updateMobile);
    }, []);

    useEffect(() => {
        if (currentIndex >= totalSlides) {
            setCurrentIndex(0);
            setPage([0, 0]);
        }
    }, [totalSlides, currentIndex]);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"],
    });

    const liftY = useTransform(
        scrollYProgress,
        isMobile ? SCROLL_OFFSETS_MOBILE : SCROLL_OFFSETS_DESKTOP,
        [viewportHeight, 0, 0, -viewportHeight],
        { clamp: false },
    );
    const smoothLiftY = useSpring(liftY, springConfig.default);

    const paginate = useCallback((newDirection: number) => {
        const newIndex = currentIndex + newDirection;
        if (newIndex >= 0 && newIndex < totalSlides) {
            setCurrentIndex(newIndex);
            setPage([newIndex, newDirection]);
        }
    }, [currentIndex, totalSlides]);

    const swipePower = useCallback((offset: number, velocity: number) => {
        return Math.abs(offset) * velocity;
    }, []);

    const handleDragEnd = useCallback((e: MouseEvent | TouchEvent | PointerEvent, { offset, velocity }: PanInfo) => {
        const swipe = swipePower(offset.x, velocity.x);

        if (swipe < -SWIPE_CONFIDENCE_THRESHOLD && currentIndex < totalSlides - 1) {
            paginate(1);
        } else if (swipe > SWIPE_CONFIDENCE_THRESHOLD && currentIndex > 0) {
            paginate(-1);
        }
    }, [currentIndex, totalSlides, paginate, swipePower]);

    const getCurrentCards = useCallback(() => {
        const startIndex = currentIndex * cardsPerSlide;
        return cardsData.slice(startIndex, startIndex + cardsPerSlide);
    }, [currentIndex, cardsPerSlide]);

    return (
        <section ref={sectionRef} className="relative w-full ">
            <span id="arbeiten" className="block h-0 w-0" aria-hidden="true" />
            <motion.div
                initial={false}
                className="arbeiten-wrapper  fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[98%] sm:w-[90%] max-w-[1400px] flex items-center justify-center overflow-visible z-[90]  lg:mt-10"
                style={{ y: smoothLiftY }}
            >
                <div className="arbeiten-container w-full flex flex-col relative z-10 rounded-[24px] lg:rounded-[40px] border-[4px] sm:border-[6px] lg:border-[10px] border-[#1D2B4F] p-4 pb-3 lg:p-5 lg:pb-4 xl:p-5 xl:pb-4 2xl:p-5 2xl:pb-4" style={{ background: 'var(--gradient-beige-diagonal)' }}>
                    <div className="flex flex-col xl:flex-row items-center justify-center w-full max-w-[800px] xl:max-w-full mx-auto relative mb-1 xs:mb-4 lg:mb-4 xl:mb-3 2xl:mb-4 gap-2 xs:gap-4 lg:gap-4 xl:gap-3">
                        {/* Megafon - Mobile/Tablet: oben rechts, Desktop: links */}
                        <div className="arbeiten-megafon absolute xl:relative -right-4 xs:left-5 lg:left-6 xl:left-0 top-7 xl:top-0 -translate-y-1/2 xl:translate-y-0 w-[80px] xs:w-[100px] sm:w-[140px] lg:w-[180px] xl:w-[220px] h-[80px] xs:h-[100px] sm:h-[140px] lg:h-[180px] xl:h-[220px] opacity-100 pointer-events-none z-0 xl:z-10 xl:order-1 xl:flex-shrink-0 ">
                            <img
                                src="/img/megafonMöwe.webp"
                                alt="Megafon Möwe"
                                className="w-full h-full object-contain"
                                draggable="false"
                            />
                        </div>
                        
                        {/* Text Content - Desktop: mittig-rechts */}
                        <div className="flex flex-col items-start justify-center w-full relative z-10 xl:order-2 xl:flex-1 xl:max-w-[700px]">
                            <h2 className="arbeiten-header text-2xl xs:text-3xl lg:text-4xl xl:text-5xl text-white stroke-text mb-0 lg:mb-2 xl:mb-3 text-center xl:text-left w-full">Meine Arbeiten</h2>
                            <p className="arbeiten-description text-[11px] xs:text-[12px] lg:text-[14px] xl:text-[16px] leading-[12px] xs:leading-[13px] lg:leading-[20px] xl:leading-[26px] text-black text-left font-[Poppins] font-medium mb-1 xs:mb-3 lg:mb-3 xl:mb-4 max-w-[90%] xl:max-w-full">
                                Einzigartige und maßgeschneiderte digitale Lösungen – von modernen
                                Webseiten über funktionale Webanwendungen bis hin zu interaktiven
                                Erlebnissen. Jedes Projekt ist eine neue Gelegenheit, Kreativität
                                und technische Präzision zu verbinden.
                            </p>
                            <div className="flex justify-end w-full">
                                <motion.a
                                    whileHover={{ y: -6, rotate: -1 }}
                                    whileTap={{ scale: 0.96 }}
                                    animate={{
                                        x: [-2, 2, -2],
                                    }}
                                    transition={{
                                        x: {
                                            duration: 1.5,
                                            repeat: Infinity,
                                            ease: "easeInOut",
                                        }
                                    }}
                                    href="#kontakt"
                                    className="arbeiten-button inline-flex items-center justify-center gap-2 rounded-sm border-[2px] lg:border-[4px] border-[var(--dark-blue)] bg-[var(--yellow)] px-3 py-1 xs:py-2 lg:px-4 lg:py-2.5 text-[11px] xs:text-sm lg:text-base font-[Poppins] font-bold text-[var(--dark-blue)] shadow-[2px_2px_0_var(--dark-blue)] lg:shadow-[3px_3px_0_var(--dark-blue)] transition-colors duration-200 hover:bg-[var(--light-blue)] hover:text-white pointer-events-auto -mt-3 mr-3 lg:-mt-2 lg:mr-6 ">
                                    <div className="whitespace-normal">
                                    <span className="hidden sm:inline">Jetzt&nbsp;</span>Kontakt<span className="hidden sm:inline">&nbsp;aufnehmen!</span>
                                    </div>
                                </motion.a>
                            </div>
                        </div>
                    </div>

                    <div className="relative w-full max-w-[1200px] mx-auto overflow-visible">
                        <div className="arbeiten-cards-wrapper relative min-h-[280px] xs:min-h-[420px] lg:min-h-[420px] xl:min-h-[350px] 2xl:min-h-[380px]">
                            <AnimatePresence initial={false} custom={direction} mode="popLayout">
                                <motion.div
                                    key={currentIndex}
                                    custom={direction}
                                    variants={{
                                        enter: (direction: number) => ({
                                            x: direction > 0 ? 1200 : -1200,
                                            opacity: 0,
                                        }),
                                        center: {
                                            zIndex: 1,
                                            x: 0,
                                            opacity: 1,
                                        },
                                        exit: (direction: number) => ({
                                            zIndex: 0,
                                            x: direction < 0 ? 1200 : -1200,
                                            opacity: 0,
                                        })
                                    }}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{
                                        x: { type: "spring", stiffness: 300, damping: 30 },
                                        opacity: { duration: 0.3 },
                                    }}
                                    drag="x"
                                    dragConstraints={{ left: 0, right: 0 }}
                                    dragElastic={0.5}
                                    onDragEnd={handleDragEnd}
                                    className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 cursor-grab active:cursor-grabbing"
                                >
                                    {getCurrentCards().map((card, index) => (
                                        <motion.div
                                            key={currentIndex * cardsPerSlide + index}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.1, duration: 0.4 }}
                                            whileHover={{ y: -8, scale: 1.02 }}
                                            className="flex flex-col border-[3px] lg:border-[4px] border-dashed border-[#1D2B4F] rounded-[12px] lg:rounded-[20px] overflow-hidden bg-stripes-pattern shadow-[4px_4px_0_rgba(0,0,0,0.4)]"
                                        >
                                            <div className="arbeiten-card-image w-full h-[130px] xs:h-[160px] lg:h-[200px] xl:h-[170px] 2xl:h-[200px] border-dashed border-[#1D2B4F] overflow-hidden bg-white">
                                                <img 
                                                    className="w-full h-full object-cover object-top pointer-events-none" 
                                                    src={card.image} 
                                                    alt={card.title}
                                                    draggable="false"
                                                />
                                            </div>
                                            <div className="arbeiten-card-content flex flex-col items-start justify-start flex-1 p-2 xs:p-3 lg:p-4">
                                                <h2 className="arbeiten-card-subtitle text-[10px] xs:text-[11px] lg:text-[12px] leading-[12px] xs:leading-[14px] lg:leading-[16px] text-black font-[Poppins] font-semibold uppercase tracking-wider mb-0 sm:mb-1">
                                                    {card.subtitle}
                                                </h2>
                                                <h3 
                                                    className="arbeiten-card-title text-[18px] xs:text-[20px] lg:text-[24px] font-bold text-white tracking-[0.5px] font-luckiest leading-[1.1] mb-1 xs:mb-1.5 lg:mb-2"
                                                    style={{
                                                        textShadow: '2px 2px 4px rgba(9, 9, 10, 0.9), 0 0 10px rgba(255, 224, 102, 0.4), 0 4px 7px rgba(0, 0, 0, 0.9)',
                                                        WebkitTextStroke: '0.5px rgba(9, 9, 9, 0.9)'
                                                    }}
                                                >
                                                    {card.title}
                                                </h3>
                                                <p className="arbeiten-card-description text-[12px] xs:text-[13px] lg:text-[14px] leading-[16px] xs:leading-[18px] lg:leading-[20px] font-[Poppins] font-medium text-black mb-1.5 xs:mb-2 lg:mb-3">
                                                    {card.description}
                                                </p>
                                                <a 
                                                    className="arbeiten-card-link mt-auto text-black text-[12px] xs:text-[13px] lg:text-[14px] font-[Poppins] font-bold hover:text-[#1d2b4f] transition-colors inline-block pointer-events-auto underline decoration-2 underline-offset-4" 
                                                    href={card.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                >
                                                    Website besuchen →
                                                </a>
                                            </div>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                        
                        {/* Navigation Arrows - Desktop: sides, Mobile: bottom */}
                        {currentIndex > 0 && (
                            <motion.button
                                whileHover={{ scale: 1.15, x: -2 }}
                                whileTap={{ scale: 0.9 }}
                                animate={{ x: [-3, 0, -3] }}
                                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                                onClick={() => paginate(-1)}
                                className="hidden lg:flex absolute left-[-80px] top-1/2 -translate-y-1/2 z-[70] w-14 h-14 rounded-full bg-[#1d2b4f] text-white items-center justify-center shadow-[4px_4px_0_rgba(29,43,79,0.3)] hover:bg-[#FFE066] hover:text-[#1d2b4f] transition-all duration-200"
                                aria-label="Vorherige Karten"
                            >
                                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                                </svg>
                            </motion.button>
                        )}
                        
                        {currentIndex < totalSlides - 1 && (
                            <motion.button
                                whileHover={{ scale: 1.15, x: 2 }}
                                whileTap={{ scale: 0.9 }}
                                animate={{ x: [3, 0, 3] }}
                                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                                onClick={() => paginate(1)}
                                className="hidden lg:flex absolute right-[-80px] top-1/2 -translate-y-1/2 z-[70] w-14 h-14 rounded-full bg-[#1d2b4f] text-white items-center justify-center shadow-[4px_4px_0_rgba(29,43,79,0.3)] hover:bg-[#FFE066] hover:text-[#1d2b4f] transition-all duration-200"
                                aria-label="Nächste Karten"
                            >
                                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                </svg>
                            </motion.button>
                        )}
                        
                        {/* Mobile Navigation Arrows - Below cards */}
                        <div className="hidden xs:flex lg:hidden absolute bottom-[-60px] xs:bottom-[-90px] left-1/2 -translate-x-1/2 gap-4 z-[70]">
                            {currentIndex > 0 && (
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => paginate(-1)}
                                    className="w-12 h-12 rounded-full bg-[#1d2b4f] text-white flex items-center justify-center shadow-[3px_3px_0_rgba(29,43,79,0.3)]"
                                    aria-label="Vorherige Karte"
                                >
                                    <svg className="w-5 h-5 xs:w-6 xs:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </motion.button>
                            )}
                            {currentIndex < totalSlides - 1 && (
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => paginate(1)}
                                    className="w-12 h-12 rounded-full bg-[#1d2b4f] text-white flex items-center justify-center shadow-[3px_3px_0_rgba(29,43,79,0.3)]"
                                    aria-label="Nächste Karte"
                                >
                                    <svg className="w-5 h-5 xs:w-6 xs:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                    </svg>
                                </motion.button>
                            )}
                        </div>

                        {/* Extra small Mobile Navigation Arrows - Left and Right */}
                        <div className="xs:hidden lg:hidden absolute bottom-1/2 -translate-y-[80px] w-full justify-between px-2 z-[70] pointer-events-none">
                            {currentIndex > 0 && (
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => paginate(-1)}
                                    className="w-12 h-12 rounded-full bg-[#1d2b4f] text-white flex items-center justify-center shadow-[3px_3px_0_rgba(29,43,79,0.3)] pointer-events-auto absolute -left-3.5"
                                    aria-label="Vorherige Karte"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                                    </svg>
                                </motion.button>
                            )}
                            {currentIndex < totalSlides - 1 && (
                                <motion.button
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => paginate(1)}
                                    className="w-12 h-12 rounded-full bg-[#1d2b4f] text-white flex items-center justify-center shadow-[3px_3px_0_rgba(29,43,79,0.3)] pointer-events-auto absolute -right-3.5 "
                                    aria-label="Nächste Karte"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                                    </svg>
                                </motion.button>
                            )}
                        </div>
                        
                        {/* Slide Navigation Indicators */}
                        {totalSlides > 1 && (
                            <div className="absolute bottom-[-25px] xs:bottom-[-120px] lg:bottom-[-40px] left-1/2 -translate-x-1/2 flex gap-2 xs:gap-2.5 z-[70] bg-white/20 backdrop-blur-sm px-3 py-2 xs:px-4 xs:py-2.5 rounded-full">
                                {Array.from({ length: totalSlides }).map((_, i) => (
                                    <motion.button
                                        key={i}
                                        onClick={() => {
                                            const newDirection = i > currentIndex ? 1 : -1;
                                            setCurrentIndex(i);
                                            setPage([i, newDirection]);
                                        }}
                                        whileHover={{ scale: 1.2 }}
                                        whileTap={{ scale: 0.9 }}
                                        className={`h-2 xs:h-2.5 rounded-full transition-all duration-300 ${
                                            i === currentIndex 
                                                ? 'w-8 xs:w-10 bg-[#1d2b4f]' 
                                                : 'w-2 xs:w-2.5 bg-[#1d2b4f]/40 hover:bg-[#1d2b4f]/70'
                                        }`}
                                        aria-label={`Gehe zu Slide ${i + 1}`}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </motion.div>
            <div className="h-[120vh]" aria-hidden="true" />
        </section>
    );
};

export default Arbeiten;


